// BOORO: where everything sits on the team canvas. Offices hold their groups,
// groups hold subgroups and agents. A group or an office the person moved keeps
// its place; everything else is placed by the canvas. Containers grow with
// what they hold. The Chief sits above everything, outside every office.

export const AGENT_WIDTH = 200;
export const AGENT_HEIGHT = 60;
export const CHIEF_WIDTH = 300;
export const CHIEF_HEIGHT = 72;
export const CHILD_GAP = 16;
const GAP = CHILD_GAP;
export const PADDING = 16;
export const HEADER = 48;
const MIN_AGENTS_PER_ROW = 2;
/** The room between a lead and its people, for the line that joins them. */
export const LEAD_GAP = 32;
/** Containers side by side wrap into a new row beyond this width. */
const MAX_ROW_WIDTH = 1100;
const OFFICE_GAP = 48;
const OFFICE_MAX_ROW_WIDTH = 3200;
const MIN_CONTAINER_WIDTH = 260;
/** How far above the offices the Chief sits. */
const CHIEF_ABOVE = 140;

export type LayoutNode = {
  id: string;
  kind: "office" | "group" | "agent" | "chief";
  /** The container the node sits in; positions are relative to it. */
  parentId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  /** For containers: where their subgroups start, the origin of stored places. */
  childrenTop?: number;
  /** For agents: whether the agent leads the group or the office it sits in. */
  lead?: boolean;
};

type Group = {
  id: string;
  name?: string;
  spaceId: string;
  parentGroupId: string | null;
  position: number;
  leadBotId?: string | null;
  x?: number | null;
  y?: number | null;
};
type Agent = { id: string; spaceId: string; groupId: string | null; position: number };
type Office = {
  id: string;
  name?: string;
  x?: number | null;
  y?: number | null;
  /** The agent that leads the office, under the Chief. */
  leadBotId?: string | null;
};

/** The node id of an office, a group or an agent; ids of different kinds never collide. */
export const officeNodeId = (spaceId: string) => `office:${spaceId}`;
export const groupNodeId = (groupId: string) => `group:${groupId}`;
export const agentNodeId = (botId: string) => `agent:${botId}`;

/** Lays out boxes in rows that wrap; returns their offsets and the size of the whole. */
function flow(
  sizes: readonly { width: number; height: number }[],
  maxWidth: number,
  gap = GAP,
): { offsets: { x: number; y: number }[]; width: number; height: number } {
  const offsets: { x: number; y: number }[] = [];
  let x = 0;
  let y = 0;
  let rowHeight = 0;
  let width = 0;
  for (const size of sizes) {
    if (x > 0 && x + size.width > maxWidth) {
      x = 0;
      y += rowHeight + gap;
      rowHeight = 0;
    }
    offsets.push({ x, y });
    x += size.width + gap;
    rowHeight = Math.max(rowHeight, size.height);
    width = Math.max(width, x - gap);
  }
  return { offsets, width, height: sizes.length > 0 ? y + rowHeight : 0 };
}

/**
 * How wide a header must be to show its name in full, next to its icon, its
 * count and its buttons. An estimate is enough: names rarely run long.
 */
function headerWidth(name: string | undefined, kind: "office" | "group"): number {
  const text = (name?.length ?? 0) * 8.5;
  return Math.ceil(text + (kind === "office" ? 190 : 150));
}

const placed = (item: { x?: number | null; y?: number | null }) =>
  typeof item.x === "number" && typeof item.y === "number";

/**
 * The canvas: every office as a box, its groups nested inside, the agents as
 * cards in their group with the lead first. Groups whose parent is gone sit at
 * the top of their office, agents of a group that is gone directly in it.
 */
export function layoutOrg(input: {
  offices: readonly Office[];
  groups: readonly Group[];
  agents: readonly Agent[];
  chiefBotId?: string | null;
}): LayoutNode[] {
  const nodes: LayoutNode[] = [];
  const groupIds = new Set(input.groups.map((group) => group.id));
  const childGroups = new Map<string, Group[]>();
  const childAgents = new Map<string, Agent[]>();
  const leadOf = new Map<string, string | null>();
  const add = <Item>(map: Map<string, Item[]>, key: string, item: Item) => {
    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  };
  for (const office of input.offices) {
    leadOf.set(officeNodeId(office.id), office.leadBotId ?? null);
  }
  for (const group of input.groups) {
    leadOf.set(groupNodeId(group.id), group.leadBotId ?? null);
    const parent =
      group.parentGroupId && groupIds.has(group.parentGroupId)
        ? groupNodeId(group.parentGroupId)
        : officeNodeId(group.spaceId);
    add(childGroups, parent, group);
  }
  for (const agent of input.agents) {
    // The Chief stands above everything, not inside an office.
    if (agent.id === input.chiefBotId) continue;
    const parent =
      agent.groupId && groupIds.has(agent.groupId)
        ? groupNodeId(agent.groupId)
        : officeNodeId(agent.spaceId);
    add(childAgents, parent, agent);
  }

  const visiting = new Set<string>();
  const container = (
    id: string,
    kind: "office" | "group",
    parentId: string | null,
    name: string | undefined,
  ): { node: LayoutNode; size: { width: number; height: number } } => {
    visiting.add(id);
    const node: LayoutNode = { id, kind, parentId, x: 0, y: 0, width: 0, height: 0 };
    nodes.push(node);

    const leadId = leadOf.get(id) ?? null;
    const agents = [...(childAgents.get(id) ?? [])].sort((a, b) => a.position - b.position);
    const lead = agents.find((agent) => agent.id === leadId) ?? null;
    const others = agents.filter((agent) => agent !== lead);
    const agentNode = (agentId: string, isLead = false): LayoutNode => {
      const entry: LayoutNode = {
        id: agentNodeId(agentId),
        kind: "agent",
        parentId: id,
        x: PADDING,
        y: 0,
        width: AGENT_WIDTH,
        height: AGENT_HEIGHT,
        ...(isLead ? { lead: true } : {}),
      };
      nodes.push(entry);
      return entry;
    };
    const leadNode = lead ? agentNode(lead.id, true) : null;
    const otherNodes = others.map((agent) => agentNode(agent.id));
    const groups = [...(childGroups.get(id) ?? [])]
      .sort((a, b) => a.position - b.position)
      // A broken tree never loops forever.
      .filter((group) => !visiting.has(groupNodeId(group.id)));
    const children = groups.map((group) => ({
      group,
      ...container(groupNodeId(group.id), "group", id, group.name),
    }));
    const maxRow = kind === "office" ? OFFICE_MAX_ROW_WIDTH : MAX_ROW_WIDTH;

    // Every child row starts below a clear gap for the hierarchy connectors.
    let top = HEADER + GAP;
    let contentWidth = 0;
    let bottom = 0;
    if (leadNode) {
      // Everyone the lead leads stands on one level under it, agents and groups
      // alike, so the organigram reads as one tree. The canvas places them.
      leadNode.y = top;
      const items = [
        ...otherNodes.map((entry) => ({ node: entry, size: entry })),
        ...children.map((child) => ({ node: child.node, size: child.size })),
      ];
      top += AGENT_HEIGHT + (items.length > 0 ? LEAD_GAP : GAP);
      const row = flow(
        items.map((item) => item.size),
        maxRow,
      );
      let firstRow = 0;
      items.forEach((item, index) => {
        const offset = row.offsets[index] ?? { x: 0, y: 0 };
        item.node.x = PADDING + offset.x;
        item.node.y = top + offset.y;
        if (offset.y === 0) firstRow = Math.max(firstRow, offset.x + item.size.width);
      });
      // Centred above the people it leads.
      leadNode.x = PADDING + Math.max(0, (firstRow - AGENT_WIDTH) / 2);
      contentWidth = Math.max(AGENT_WIDTH, row.width);
      bottom = row.height;
    } else {
      // Groups the person placed keep their place; the others flow below them.
      let right = 0;
      for (const child of children) {
        if (!placed(child.group)) continue;
        right = Math.max(right, (child.group.x ?? 0) + child.size.width);
        bottom = Math.max(bottom, (child.group.y ?? 0) + child.size.height);
      }
      const loose = children.filter((child) => !placed(child.group));
      const looseTop = bottom > 0 ? bottom + GAP : 0;
      const looseFlow = flow(
        loose.map((child) => child.size),
        maxRow,
      );
      right = Math.max(right, looseFlow.width);
      if (loose.length > 0) bottom = looseTop + looseFlow.height;

      // Agents fill the width the box has anyway, at least two to a row.
      const room = Math.max(right, headerWidth(name, kind) - 2 * PADDING);
      const perRow = Math.max(MIN_AGENTS_PER_ROW, Math.floor((room + GAP) / (AGENT_WIDTH + GAP)));
      const cols = Math.min(others.length, perRow);
      const rows = Math.ceil(others.length / perRow);
      otherNodes.forEach((entry, index) => {
        entry.x = PADDING + (index % perRow) * (AGENT_WIDTH + GAP);
        entry.y = top + Math.floor(index / perRow) * (AGENT_HEIGHT + GAP);
      });
      if (rows > 0) {
        top += rows * AGENT_HEIGHT + (rows - 1) * GAP + GAP;
        contentWidth = cols * AGENT_WIDTH + (cols - 1) * GAP;
      }
      for (const child of children) {
        if (!placed(child.group)) continue;
        child.node.x = PADDING + (child.group.x ?? 0);
        child.node.y = top + (child.group.y ?? 0);
      }
      loose.forEach((child, index) => {
        child.node.x = PADDING + (looseFlow.offsets[index]?.x ?? 0);
        child.node.y = top + looseTop + (looseFlow.offsets[index]?.y ?? 0);
      });
      contentWidth = Math.max(contentWidth, right);
    }
    node.childrenTop = top;

    const width = Math.max(
      MIN_CONTAINER_WIDTH,
      headerWidth(name, kind),
      contentWidth + 2 * PADDING,
    );
    const empty = agents.length === 0 && children.length === 0;
    const height = top + bottom + (bottom > 0 ? PADDING : 0) + (empty ? 24 : 0);
    node.width = width;
    node.height = Math.max(height, HEADER + PADDING);
    return { node, size: { width, height: node.height } };
  };

  // Offices the person placed keep their place; the others line up to their right.
  const offices = input.offices.map((office) => ({
    office,
    ...container(officeNodeId(office.id), "office", null, office.name),
  }));
  let right = 0;
  let top = Number.POSITIVE_INFINITY;
  for (const entry of offices) {
    if (!placed(entry.office)) continue;
    entry.node.x = entry.office.x ?? 0;
    entry.node.y = entry.office.y ?? 0;
    right = Math.max(right, entry.node.x + entry.size.width);
    top = Math.min(top, entry.node.y);
  }
  const loose = offices.filter((entry) => !placed(entry.office));
  const startX = right > 0 ? right + OFFICE_GAP : 0;
  const startY = Number.isFinite(top) ? top : 0;
  const looseFlow = flow(
    loose.map((entry) => entry.size),
    OFFICE_MAX_ROW_WIDTH,
    OFFICE_GAP,
  );
  loose.forEach((entry, index) => {
    entry.node.x = startX + (looseFlow.offsets[index]?.x ?? 0);
    entry.node.y = startY + (looseFlow.offsets[index]?.y ?? 0);
  });

  if (input.chiefBotId && input.agents.some((agent) => agent.id === input.chiefBotId)) {
    const officeNodes = offices.map((entry) => entry.node);
    const minX = Math.min(...officeNodes.map((node) => node.x), 0);
    const maxX = Math.max(...officeNodes.map((node) => node.x + node.width), CHIEF_WIDTH);
    const minY = Math.min(...officeNodes.map((node) => node.y), 0);
    nodes.push({
      id: agentNodeId(input.chiefBotId),
      kind: "chief",
      parentId: null,
      x: Math.round((minX + maxX) / 2 - CHIEF_WIDTH / 2),
      y: minY - CHIEF_ABOVE,
      width: CHIEF_WIDTH,
      height: CHIEF_HEIGHT,
    });
  }
  return nodes;
}
